/* eslint-disable no-restricted-syntax */
import {
  ChatCircleDotsIcon,
  DotsThreeVerticalIcon,
  MusicNoteIcon,
  PlusIcon,
  ShareFatIcon,
  ThumbsDownIcon,
  ThumbsUpIcon,
} from '@phosphor-icons/react';
import React from 'react';

import { MediaSlot } from './MediaSlot';
import { PreviewAvatar } from './PreviewAvatar';
import { DEFAULT_PREVIEW_BRAND_COLOR, type PlatformPreviewBaseProps, compactNumber } from './types';

import { cn } from '@/lib/utils';

/**
 * YouTube Short — vertical 9:16 frame with right-rail action stack
 * (like, dislike, comment, share, sound) and bottom-left channel + caption.
 * Replicates the Shorts player feel.
 */
export const YouTubeShort: React.FC<PlatformPreviewBaseProps & { soundCredit?: string }> = ({
  brandName,
  username,
  brandColor = DEFAULT_PREVIEW_BRAND_COLOR,
  avatarUrl,
  caption,
  imageUrl,
  videoUrl,
  videoPoster,
  focalX,
  focalY,
  zoom,
  engagement,
  isLoading = false,
  soundCredit,
  mediaOverlay,
  className,
}) => {
  const handle = (username || brandName).toLowerCase().replace(/\s+/g, '');
  const likes = engagement?.likes;
  const comments = engagement?.comments;
  const shares = engagement?.shares;

  return (
    <article
      className={cn('relative w-full aspect-[9/16] rounded-xl overflow-hidden bg-black', className)}
      data-platform="youtube-short"
    >
      <MediaSlot
        fill
        bg="zinc"
        videoFirst
        emptyHint="Your short here"
        imageUrl={imageUrl}
        videoUrl={videoUrl}
        videoPoster={videoPoster}
        focalX={focalX}
        focalY={focalY}
        zoom={zoom}
        isLoading={isLoading}
        alt={caption ?? `${brandName} short`}
        overlay={mediaOverlay}
      />

      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/70 pointer-events-none" />

      <div className="absolute top-3 left-3 right-3 flex items-center justify-between text-white">
        <span className="text-caption font-medium">Shorts</span>
        <DotsThreeVerticalIcon className="size-5" weight="bold" />
      </div>

      <div className="absolute right-2 bottom-20 flex flex-col items-center gap-4 text-white">
        <YtAction
          icon={<ThumbsUpIcon className="size-7" weight="fill" />}
          label={likes == null ? undefined : compactNumber(likes)}
        />
        <YtAction icon={<ThumbsDownIcon className="size-7" weight="regular" />} label="Dislike" />
        <YtAction
          icon={<ChatCircleDotsIcon className="size-7" weight="fill" />}
          label={comments == null ? undefined : compactNumber(comments)}
        />
        <YtAction
          icon={<ShareFatIcon className="size-7" weight="fill" />}
          label={shares == null ? undefined : compactNumber(shares)}
        />
        <div className="size-9 rounded-md bg-white/10 backdrop-blur-sm flex items-center justify-center">
          <MusicNoteIcon className="size-4" weight="fill" />
        </div>
      </div>

      <div className="absolute left-3 right-16 bottom-3 text-white space-y-2">
        <div className="flex items-center gap-2">
          <PreviewAvatar
            brandName={brandName}
            brandColor={brandColor}
            avatarUrl={avatarUrl}
            className="size-7 rounded-full"
            textClassName="text-detail font-semibold"
          />
          <span className="text-caption font-medium truncate">@{handle}</span>
          <button
            type="button"
            className="ml-1 text-micro font-semibold uppercase tracking-wider rounded-full bg-white text-black px-2.5 py-1 hover:bg-white/90 transition-colors inline-flex items-center gap-1"
          >
            <PlusIcon className="size-2.5" weight="bold" /> Subscribe
          </button>
        </div>
        {caption ? <p className="text-detail leading-relaxed line-clamp-2">{caption}</p> : null}
        {soundCredit ? (
          <div className="flex items-center gap-1.5 text-detail text-white/85">
            <MusicNoteIcon className="size-3 shrink-0" weight="fill" />
            <span className="truncate">{soundCredit}</span>
          </div>
        ) : null}
      </div>
    </article>
  );
};

const YtAction: React.FC<{ icon: React.ReactNode; label?: string }> = ({ icon, label }) => (
  <div className="flex flex-col items-center gap-0.5">
    <span
      className="size-11 rounded-full bg-white/15 backdrop-blur-sm flex items-center justify-center"
      aria-hidden
    >
      {icon}
    </span>
    {label != null ? <span className="text-micro font-medium">{label}</span> : null}
  </div>
);

YouTubeShort.displayName = 'YouTubeShort';
